// Casino Dashboard data service.
//
// Ported from casino_dashboard/src/services/dashboardService.js. It talks to the
// same backend as the rest of the admin (the /dashboard/* reporting endpoints
// live on the account API), so it reuses `axiosInstance` and therefore inherits
// the SessonId request interceptor and the global 401 handling in App.js.
import { axiosInstance } from "./axiosInstances";

// GET a JSON payload. The reporting endpoints answer { status, data }.
export const getData = async (endpoint) => {
  const response = await axiosInstance.get(endpoint);
  return response.data;
};

// GET a binary payload (CSV / PDF export).
export const getDownloadData = async (endpoint) => {
  const response = await axiosInstance.get(endpoint, {
    responseType: "blob",
    // axiosInstance defaults to `Accept: application/json`, but the report
    // endpoints declare `produces = application/octet-stream`. Spring finds no
    // converter for the requested type and answers 406 Not Acceptable, so the
    // Accept header has to admit the binary payload. (No Content-Type here:
    // a GET carries no body.)
    headers: { Accept: "*/*" },
  });
  return response.data;
};

export const postData = async (endpoint, data) => {
  const response = await axiosInstance.post(endpoint, data);
  return response.data;
};

// Trigger a browser download for a blob returned by getDownloadData.
export const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// Pull a readable message out of a blob body. With responseType "blob" an error
// response arrives as a Blob too, so `error.response.data.message` is always
// undefined and the admin only ever sees "Request failed with status code 500".
const blobMessage = async (blob, fallback) => {
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text);
    return parsed?.message || parsed?.error || fallback;
  } catch (e) {
    return fallback;
  }
};

// Fetch + save in one step; resolves to true on success.
export const downloadFile = async (endpoint, filename) => {
  let blob;
  try {
    blob = await getDownloadData(endpoint);
  } catch (e) {
    const status = e?.response?.status;
    const fallback = status
      ? `Download failed (HTTP ${status}).`
      : e?.message || "Download failed.";
    const body = e?.response?.data;
    throw new Error(
      body instanceof Blob ? await blobMessage(body, fallback) : body?.message || fallback
    );
  }
  // A failure can still arrive as 200 + a JSON body. Saving that verbatim would
  // hand the admin a .csv full of an error object, so surface it as an error
  // instead of a corrupt download.
  if (blob && blob.type && blob.type.includes("json")) {
    throw new Error(await blobMessage(blob, "Download failed."));
  }
  saveBlob(blob, filename);
  return true;
};

const DashboardService = { getData, getDownloadData, postData, saveBlob, downloadFile };

export default DashboardService;
